'use client';

import React, { useContext, useEffect, useRef } from 'react';
import useScrollDirection from 'ui/src/hooks/useScrollDirection';
import useClickOutside from 'ui/src/hooks/useClickOutside';
import twMerge from 'utilities/src/twMerge';
import Links from './Links';
import BackgroundWrapper from './_BackgroundWrapper';
import ThemeProvider from './_ThemeProvider';
import DropdownContext from './_dropdownContext';
import ShadowContainer from './_ShadowContainer';

interface Props {
  links: { label: string; href: string }[][];
  className?: string;
  linksWrapperClassName?: string;
  linksClassName?: string;
  linksListClassName?: string;
  PrevListSibling?: React.ReactNode;
  children?: React.ReactNode;
  navRef?: React.RefObject<HTMLElement | null>;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  headerButtonId: string;
}

export default function SecondaryNavigation({
  links,
  className,
  linksWrapperClassName,
  PrevListSibling,
  children,
  linksClassName,
  linksListClassName,
  navRef,
  onBlur,
  headerButtonId,
}: Props) {
  const { isScrollingDown: isInvisible } = useScrollDirection();
  const internalNavRef = useRef<HTMLElement>(null);
  const { isOpen, setIsOpen } = useContext(DropdownContext);
  const headerButton = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (headerButton.current) return;
    headerButton.current = document.getElementById(headerButtonId) as HTMLButtonElement;
  }, [headerButtonId]);
  const { clickedOutside } = useClickOutside(navRef || internalNavRef, headerButton);

  useEffect(() => {
    if (clickedOutside && isOpen) {
      setIsOpen(false);
    }
  }, [clickedOutside, isOpen, setIsOpen]);

  useEffect(() => {
    if (isInvisible) {
      setIsOpen(false);
    }
  }, [isInvisible, setIsOpen]);

  return (
    <ThemeProvider>
      <nav
        ref={navRef || internalNavRef}
        className={twMerge(
          'site-paragraph-l mdS:site-paragraph-s md:site-paragraph-m mdS:top-24 fixed top-20 z-30 w-full',
          'transition-all duration-300',
          {
            'mdS:pointer-events-none mdS:opacity-0': isInvisible,
          },
        )}
        onBlur={onBlur}
      >
        <div
          className={twMerge(
            'mdS:contents grid transition-[grid-template-rows] duration-500',
            { 'max-mdS:grid-rows-[0fr]': !isOpen },
            { 'grid-rows-[1fr]': isOpen },
          )}
        >
          <ShadowContainer
            className={twMerge(
              'mdS:overflow-visible overflow-hidden',
              'mdL:flex-row relative flex flex-col-reverse',
              className,
            )}
          >
            <div
              className={twMerge(
                'mdS:flex mdS:flex-row-reverse mdS:h-14 mdL:h-auto mdL:flex-1 mdS:rounded-r-lg mdL:min-w-0 mdS:mb-0 mb-10 overflow-hidden',
                'mdS:rounded-b-lg rounded-b-2xl',
                linksWrapperClassName,
              )}
            >
              {PrevListSibling}
              <div className={twMerge('mdS:flex-1 grid grid-rows-[1fr] overflow-hidden', linksClassName)}>
                <BackgroundWrapper className="mdS:rounded-l-lg mdL:pr-9 mdS:min-h-14 overflow-hidden">
                  <Links links={links} className={linksListClassName} />
                </BackgroundWrapper>
              </div>
            </div>
            {children}
          </ShadowContainer>
        </div>
      </nav>
    </ThemeProvider>
  );
}
